import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Palette, Sun, Moon, Monitor, Save, CheckCircle2, ShoppingBag, Search, Package, MessageCircle } from "lucide-react";

const MODES = [
  { key: "light", label: "Light", icon: Sun, desc: "Clean white surfaces for daytime shopping" },
  { key: "dark", label: "Dark", icon: Moon, desc: "Signature charcoal & gold night theme" },
  { key: "system", label: "System Default", icon: Monitor, desc: "Follow each visitor's device setting" },
];

export default function AdminSettings() {
  const [mode, setMode] = useState("dark");
  const [saved, setSaved] = useState("dark");
  const [whatsapp, setWhatsapp] = useState("");
  const [savedWhatsapp, setSavedWhatsapp] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const m = ["light", "dark", "system"].includes(r.data?.theme_mode) ? r.data.theme_mode : "dark";
      setMode(m); setSaved(m);
      setWhatsapp(r.data?.whatsapp_number || "");
      setSavedWhatsapp(r.data?.whatsapp_number || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", { theme_mode: mode, whatsapp_number: whatsapp.trim() });
      setSaved(r.data?.theme_mode || mode);
      setSavedWhatsapp(r.data?.whatsapp_number || "");
      toast.success("Appearance settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const previewTheme = mode === "system"
    ? (window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark")
    : mode;

  const dirty = mode !== saved || whatsapp.trim() !== savedWhatsapp;

  if (loading) return <div className="py-12 text-center text-hyd-text2">Loading settings…</div>;

  return (
    <div data-testid="admin-settings-page" className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
          <Palette className="w-6 h-6 text-hyd-gold" /> Appearance
        </h1>
        <p className="text-hyd-text2 text-sm mt-1">
          Choose the default theme for the storefront. Users can still pick their own preference from the header switcher.
        </p>
      </div>

      {/* WhatsApp support number */}
      <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-hyd-gold font-bold">
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp support
        </div>
        <Label className="text-xs text-hyd-text2 block">Shown as a floating button on every storefront page</Label>
        <Input
          data-testid="whatsapp-number-input"
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          placeholder="+91 97018 30011"
          className="bg-hyd-bg border-hyd-border max-w-sm"
        />
        <p className="text-[11px] text-hyd-text3">
          Include the country code (e.g. 91 for India). Leave blank to hide the WhatsApp button.
        </p>
      </div>

      {/* Theme mode cards */}
      <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-hyd-gold font-bold">Theme mode</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {MODES.map((m) => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                data-testid={`theme-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`text-left p-4 rounded-2xl border transition-colors ${
                  active
                    ? "border-hyd-gold bg-hyd-gold/10 shadow-glow-gold"
                    : "border-hyd-border bg-hyd-bg hover:border-hyd-gold/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <m.icon className={`w-4 h-4 ${active ? "text-hyd-gold" : "text-hyd-text2"}`} />
                  <span className={`font-bold ${active ? "text-hyd-gold" : "text-hyd-text"}`}>{m.label}</span>
                  {saved === m.key && (
                    <span className="ml-auto text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-hyd-gold/20 text-hyd-gold">Current</span>
                  )}
                </div>
                <div className="text-xs text-hyd-text2 mt-1.5">{m.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Default application theme (mirrors the same setting) */}
        <div>
          <Label className="text-xs text-hyd-text2 mb-1 block">Default application theme</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="default-theme-select" className="bg-hyd-bg border-hyd-border rounded-xl max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light" data-testid="default-theme-option-light">Light</SelectItem>
              <SelectItem value="dark" data-testid="default-theme-option-dark">Dark</SelectItem>
              <SelectItem value="system" data-testid="default-theme-option-system">System</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-hyd-text3 mt-1">
            Saved to the database — applied to every visitor who hasn't chosen a personal theme.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
          </Button>
          {saved === mode && (
            <span data-testid="settings-saved-msg" className="flex items-center gap-1 text-emerald-400 text-xs font-bold uppercase tracking-widest">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {dirty && <span className="text-hyd-text3 text-xs">Unsaved changes</span>}
        </div>
      </div>

      {/* Live preview sandbox */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest text-hyd-gold font-bold">Live preview</div>
          <span className="text-[11px] text-hyd-text3">
            Showing {previewTheme}{mode === "system" ? " (your device)" : ""} — updates as you change the selection
          </span>
        </div>
        <div
          key={previewTheme}
          data-theme={previewTheme}
          data-testid="theme-preview"
          className="rounded-3xl border border-hyd-border overflow-hidden"
        >
          {/* Nav example */}
          <div className="bg-hyd-bg/80 border-b border-hyd-border px-4 py-2.5 flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-hyd-red grid place-items-center">
              <ShoppingBag className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-hyd-text">Hyderabad Crackers</span>
            <span className="ml-auto text-xs text-hyd-text2">Home</span>
            <span className="text-xs text-hyd-gold font-semibold">Quick Order</span>
            <button className="text-[10px] font-bold uppercase tracking-wider bg-hyd-gold text-hyd-bg px-3 py-1 rounded-full">
              Login
            </button>
          </div>

          <div className="bg-hyd-bg p-4 space-y-4">
            {/* Search input + buttons */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold" />
                <Input placeholder="Search sparklers, chakras…" className="pl-9 h-10 bg-hyd-panel border-2 border-hyd-gold/60 text-hyd-text placeholder:text-hyd-text3 rounded-2xl" />
              </div>
              <button className="bg-hyd-red text-white text-xs font-bold uppercase tracking-wider px-4 rounded-full shadow-glow-red hover:bg-hyd-redDim transition-colors">
                + Add
              </button>
            </div>

            {/* Product card + badge */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden">
                <div className="aspect-video bg-hyd-panel2 grid place-items-center">
                  <Package className="w-8 h-8 text-hyd-text3" />
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm text-hyd-text line-clamp-1">Golden Sparklers 15cm</div>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-hyd-gold font-bold text-sm">₹65</span>
                    <span className="text-hyd-text3 line-through text-[11px]">₹90</span>
                    <span className="text-[9px] font-bold text-emerald-500 bg-emerald-500/10 px-1 rounded">28% OFF</span>
                  </div>
                </div>
              </div>
              <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 flex flex-col justify-between">
                <div>
                  <span className="inline-block text-[9px] font-bold uppercase tracking-widest bg-hyd-gold/15 text-hyd-gold border border-hyd-gold/40 rounded-full px-2 py-0.5">
                    Bestseller
                  </span>
                  <div className="font-semibold text-sm text-hyd-text mt-2">Diwali Gift Box</div>
                  <div className="text-[11px] text-hyd-text2">Premium combo pack</div>
                </div>
                <button className="mt-2 w-full bg-hyd-gold text-hyd-bg text-xs font-bold uppercase tracking-wider py-1.5 rounded-full">
                  View
                </button>
              </div>
            </div>

            {/* Mini table */}
            <div className="bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-hyd-panel2 text-hyd-text2 uppercase tracking-widest text-[10px]">
                    <th className="text-left px-3 py-2 font-bold">Order</th>
                    <th className="text-left px-3 py-2 font-bold">Status</th>
                    <th className="text-right px-3 py-2 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hyd-border">
                  <tr>
                    <td className="px-3 py-2 text-hyd-text font-mono">HYD1234</td>
                    <td className="px-3 py-2"><span className="text-hyd-gold font-bold">Awaiting</span></td>
                    <td className="px-3 py-2 text-right text-hyd-text font-bold">₹698</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-hyd-text font-mono">HYD1235</td>
                    <td className="px-3 py-2"><span className="text-emerald-500 font-bold">Delivered</span></td>
                    <td className="px-3 py-2 text-right text-hyd-text font-bold">₹1,249</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Alert + disabled button */}
            <div className="flex items-start gap-2 p-3 rounded-xl bg-hyd-red/10 border border-hyd-red/30 text-hyd-text text-xs">
              <span className="font-bold text-hyd-red uppercase tracking-widest shrink-0">Note</span>
              Fireworks are age-restricted (18+). Confirm at checkout.
            </div>
            <div className="flex gap-2">
              <button className="flex-1 bg-hyd-gold text-hyd-bg text-xs font-bold uppercase tracking-wider py-2 rounded-full">
                Place order
              </button>
              <button disabled className="flex-1 bg-hyd-panel2 text-hyd-text3 text-xs font-bold uppercase tracking-wider py-2 rounded-full cursor-not-allowed opacity-60">
                Disabled
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
